import React from "react";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import TuneIcon from "@mui/icons-material/Tune";

export const chatHeaderActionBtnSx = {
  textTransform: "none",
  fontSize: "12px",
  fontWeight: 500,
  color: "text.secondary",
  borderRadius: "6px",
  backgroundColor: "background.paper",
  border: 1,
  borderColor: "divider",
  px: 1,
  py: 0.25,
  minWidth: 0,
  lineHeight: 1.4,
  whiteSpace: "nowrap",
  "&:hover": { backgroundColor: "action.hover" },
};

const debugActiveSx = {
  color: (theme) => (theme.palette.mode === "dark" ? "#fbbf24" : "#92400e"),
  borderColor: (theme) =>
    theme.palette.mode === "dark" ? "rgba(161,98,7,0.3)" : "#fde68a",
  backgroundColor: (theme) =>
    theme.palette.mode === "dark" ? "rgba(161,98,7,0.15)" : "#fffbeb",
  "&:hover": {
    backgroundColor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(161,98,7,0.25)" : "#fef3c7",
  },
};

const iconBtnSx = {
  width: 28,
  height: 28,
  borderRadius: "6px",
  border: 1,
  borderColor: "divider",
  backgroundColor: "background.paper",
  color: "text.secondary",
  "&:hover": { backgroundColor: "action.hover" },
};

export { iconBtnSx };

function ChatHeaderActions({
  onOpenFiles,
  onToggleDebug,
  debugEnabled = false,
  onToggleTaskQueue,
  taskQueueVisible = false,
  taskQueueOpen = false,
  onOpenConfig,
  variant = "labeled",
}) {
  const useIcons = variant === "icons";

  const taskQueueActiveSx = {
    color: "primary.main",
    borderColor: "primary.main",
    backgroundColor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(59, 130, 246, 0.15)"
        : "rgba(59, 130, 246, 0.08)",
    "&:hover": {
      backgroundColor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(59, 130, 246, 0.25)"
          : "rgba(59, 130, 246, 0.12)",
    },
  };

  const renderControl = (key, tooltip, icon, label, onClick, extraSx = {}) => {
    if (!onClick) return null;

    if (useIcons) {
      return (
        <Tooltip key={key} title={tooltip} arrow>
          <IconButton
            size="small"
            onClick={onClick}
            aria-pressed={
              key === "debug"
                ? debugEnabled
                : key === "task-queue"
                ? taskQueueOpen
                : undefined
            }
            sx={{ ...iconBtnSx, ...extraSx }}
          >
            {icon}
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <Tooltip key={key} title={tooltip} arrow>
        <Button
          onClick={onClick}
          startIcon={icon}
          sx={{ ...chatHeaderActionBtnSx, ...extraSx }}
        >
          {label}
        </Button>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
      {renderControl(
        "files",
        "Open file library",
        <FolderOpenIcon sx={{ fontSize: 16 }} />,
        "Files",
        onOpenFiles
      )}
      {renderControl(
        "debug",
        "Toggle debug mode for session diagnostics",
        <BugReportOutlinedIcon sx={{ fontSize: 16 }} />,
        "Debug",
        onToggleDebug,
        debugEnabled ? debugActiveSx : undefined
      )}
      {taskQueueVisible &&
        renderControl(
          "task-queue",
          taskQueueOpen ? "Close task queue planner" : "Open task queue planner",
          <ChecklistOutlinedIcon sx={{ fontSize: 16 }} />,
          "Task Queue",
          onToggleTaskQueue,
          taskQueueOpen ? taskQueueActiveSx : undefined
        )}
      {renderControl(
        "config",
        "Agent configuration",
        <TuneIcon sx={{ fontSize: 16 }} />,
        "Configure",
        onOpenConfig
      )}
    </Box>
  );
}

export default ChatHeaderActions;
